import { preloadClassificationContextForViewContent } from '@confluence/data-classification/entry-points/queries.ts';
import { preloadContent } from '@confluence/content-body/entry-points/preloadContent.ts';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadCustomHeaderAndFooter } from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter.ts';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadMacrosSSR } from '@confluence/fabric-extension-handlers/entry-points/preloadMacrosSSR.ts';
import { preloadSpace } from '@confluence/space-base/entry-points/preloadSpace.ts';
import { PAGE_TREE_STATUSES } from '@confluence/page-tree/entry-points/pageTreeStatuses.ts';
import { preloadPageTree } from '@confluence/page-tree/entry-points/preloadPageTree.ts';
import { preloadBlogTree } from '@confluence/blog-tree/entry-points/preloadBlogTree.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { fg } from '@confluence/feature-gating';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { preloadScreen } from '@confluence/screen/entry-points/preloadScreen.js';
import { expVal } from '@confluence/feature-experiments';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';

import { preloadGroupedButtons } from './preloadGroupedButtons.ts';

export const preloadSpaceOverviewQuery = (spaceKey: string) =>
	query<SpaceOverviewQuery$data, SpaceOverviewQuery$variables>({
		query: SpaceOverviewQuery,
		variables: {
			spaceKey,
			excludeOperations: Boolean(!isCompanyHubSpaceKey(spaceKey)),
		},
	});

type PreloadSpaceOverviewQueriesParameters = {
	spaceKey: string;
	homepageId?: string;
	previousMatch?: RouteMatch | null;
	skipPageTreePreload?: boolean;
} & Pick<Parameters<typeof preloadContent>[0], 'versionOverride'>;

const preloadSpaceOverviewQueries = async ({
	homepageId = '',
	spaceKey,
	previousMatch,
	versionOverride,
	skipPageTreePreload = false,
}: PreloadSpaceOverviewQueriesParameters): Promise<any> => {
	const tasks: Promise<any>[] = [];

	let contentNodesPromiseResolve: ((value: any) => void) | undefined = undefined;
	const contentNodesPromise = new Promise<any>((resolve) => {
		contentNodesPromiseResolve = resolve;
	});

	if (homepageId) {
		// If we know the homepageId, preload early
		tasks.push(preloadClassificationContextForViewContent(homepageId));
		if (expVal('confluence_preload_content_types_header_query', 'isEnabled', false)) {
			tasks.push(preloadContentTypesHeader(homepageId, fg('confluence_frontend_content_wrapper')));
		}

		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			tasks.push(
				preloadMacrosSSR({
					contentId: homepageId,
					useMultipleMacrosQuery: true,
					contentNodes: contentNodesPromise,
				}),
			);
		}
	}

	tasks.push(
		prepareGuardExceptionTask('ContentAndMacros', () =>
			preloadContent({
				contentId: homepageId,
				spaceKey,
				isBlog: false,
				versionOverride,
			}).then(({ result, hasErrors }): Promise<any> | void => {
				const contentId = homepageId || result?.data?.content?.nodes[0]?.id;
				// in the event that there are errors (no id from result) and the homepageId is not valid (error when loading the space),
				// bypass preloadContentPrereqs since contentId is undefined
				if (!contentId) {
					return;
				}

				contentNodesPromiseResolve?.(result?.data?.content?.nodes);

				const promises: Promise<any>[] = [];
				if (!homepageId) {
					// If we don't know the homepageId, preloadContent will tell us the id
					promises.push(
						preloadMacrosSSR({
							contentId,
							useMultipleMacrosQuery: true,
							contentNodes: result?.data?.content?.nodes,
						}),
					);
					tasks.push(preloadClassificationContextForViewContent(contentId));
					if (expVal('confluence_preload_content_types_header_query', 'isEnabled', false)) {
						tasks.push(
							preloadContentTypesHeader(contentId, fg('confluence_frontend_content_wrapper')),
						);
					}
				}
				if (hasErrors) {
					promises.push(preloadContentPrerequisites(contentId, spaceKey, { versionOverride }));
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				} else if (process.env.REACT_SSR) {
					promises.push(
						preloadMacrosSSR({
							contentId,
							useMultipleMacrosQuery: false,
							contentNodes: result?.data?.content?.nodes,
						}),
					);
				}
				return waitForAllReturnWhenError(promises);
			}),
		),
		preloadGroupedButtons({
			contentId: homepageId,
			spaceKey,
		}),
	);

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	tasks.push(preloadPageTitleContentProperties({ contentId: homepageId, spaceKey }));

	const shouldPreloadPageTree =
		!skipPageTreePreload &&
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		((process.env.REACT_SSR && !isCompanyHub) ||
			(previousMatch && previousMatch?.params?.spaceKey !== spaceKey)); // preload page tree on transition if the previous space key is different from the new space key

	if (shouldPreloadPageTree) {
		tasks.push(
			preloadPageTree({
				contentId: homepageId,
				spaceKey,
				statuses: PAGE_TREE_STATUSES,
				isSuperAdmin: false,
			}),
			preloadBlogTree({ spaceKey }),
		);
	}
	return waitForAllReturnWhenError(tasks);
};

type PreloadSpaceOverviewParameters = {
	spaceKey: string;
	homepageId: string | undefined;
	homepageIdPromise?: Promise<string | undefined>;
	previousMatch: RouteMatch | null;
} & Pick<PreloadSpaceOverviewQueriesParameters, 'versionOverride'>;

export async function preloadSpaceOverview({
	spaceKey,
	homepageId,
	homepageIdPromise,
	previousMatch,
	versionOverride,
}: PreloadSpaceOverviewParameters): Promise<any> {
	const promises = [
		preloadScreen(spaceKey) as Promise<any>,
		preloadSpace(spaceKey) as Promise<any>,
		preloadCustomHeaderAndFooter(spaceKey),
	];

	if (homepageId || homepageIdPromise) {
		// In some case we append homepageId as a query param. Then preload with it.
		// next/packages/space-overview/src/SpaceOverview.tsx still needs SpaceOverviewQuery but others are not depending on it
		promises.push(
			preloadSpaceOverviewQuery(spaceKey),
			preloadSpaceOverviewQueries({
				homepageId,
				spaceKey,
				previousMatch,
				versionOverride,
			}),
		);
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	} else if (Boolean(process.env.REACT_SSR)) {
		// Use new query to preload as space key then transform to the old queries.
		// next/packages/space-overview/src/SpaceOverview.tsx still needs SpaceOverviewQuery but others are not depending on it
		promises.push(preloadSpaceOverviewQuery(spaceKey), preloadSpaceOverviewQueries({ spaceKey }));
	} else {
		const isEarlyPageTreePreloadEnabled =
			!isCompanyHubSpaceKey(spaceKey) &&
			expVal('confluence_optimize_overview_transition_nav', 'isEnabled', false);

		if (isEarlyPageTreePreloadEnabled && previousMatch?.params?.spaceKey !== spaceKey) {
			promises.push(
				preloadPageTree({
					spaceKey,
					statuses: PAGE_TREE_STATUSES,
					isSuperAdmin: false,
					fetchPolicy: 'network-only',
				}),
				preloadBlogTree({ spaceKey, fetchPolicy: 'network-only' }),
			);
		}

		// If we have no information on home id and yet using old query, we need to fetch it first.
		promises.push(
			preloadSpaceOverviewQuery(spaceKey).then((res) => {
				// Because homepageId was provided by the function caller, it may or may
				// not be out of sync with backend:
				const actualHomepageId = res?.data?.space?.homepage?.id;
				if (actualHomepageId) {
					return preloadSpaceOverviewQueries({
						homepageId: actualHomepageId,
						spaceKey,
						previousMatch,
						versionOverride,
						skipPageTreePreload: isEarlyPageTreePreloadEnabled,
					});
				}
			}),
		);
	}

	return waitForAllReturnWhenError(promises);
}
